import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button, Form, Input, message, Modal, Popconfirm, Typography } from 'antd';
import { LinkOutlined, PlusOutlined, DeleteOutlined, CopyOutlined, AimOutlined, UserOutlined, ShareAltOutlined } from '@ant-design/icons';
import { redirectsApi, Redirect } from '../api/redirects';
import { useI18n } from '../i18n/I18nContext';
import './Redirects.css';

const { Title, Text } = Typography;

const RESERVED_SLUGS = new Set([
  'r', 'api', 'admin', 'login', 'app', 'dashboard', 'invoices', 'ledger', 'withdraws',
  'redirects', 'settings', 'how-it-works', 'why', 'me', 'track', 'new', 'create',
]);

const Redirects: React.FC = () => {
  const { t } = useI18n();
  const [list, setList] = useState<Redirect[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const stats = useMemo(() => ({
    totalClicks: list.reduce((s, r) => s + (r.totalClicks ?? 0), 0),
    totalUnique: list.reduce((s, r) => s + (r.uniqueClicks ?? 0), 0),
    linkCount: list.length,
  }), [list]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await redirectsApi.getMine();
      setList(data);
    } catch (err: any) {
      message.error(err.response?.data?.message || t.app.redirects.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFinish = async (values: { slug: string; url: string }) => {
    try {
      setSubmitting(true);
      await redirectsApi.create({ slug: values.slug.trim(), url: values.url.trim() });
      message.success(t.app.redirects.createSuccess);
      form.resetFields();
      setModalOpen(false);
      load();
    } catch (err: any) {
      message.error(err.response?.data?.message || t.app.redirects.createError);
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const onDelete = async (id: string) => {
    try {
      await redirectsApi.delete(id);
      message.success(t.app.redirects.deleteSuccess);
      load();
    } catch (err: any) {
      message.error(err.response?.data?.message || t.app.redirects.deleteError);
    }
  };

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/r/${slug}`;
    navigator.clipboard.writeText(url);
    message.success(t.app.redirects.linkCopied);
  };

  return (
    <div className="redirects-page">
      <Title level={3} className="redirects-title">
        <LinkOutlined /> {t.app.redirects.title}
      </Title>
      <p className="redirects-description">
        {t.app.redirects.description}
      </p>

      <div className="redirects-toolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal} className="redirects-add-btn" size="large">
          {t.app.redirects.newRedirect}
        </Button>
        <div className="redirects-global-stats">
          <span className="redirects-global-stat">
            <AimOutlined /> <strong>{stats.totalClicks}</strong> {t.app.redirects.clicks}
          </span>
          <span className="redirects-global-stat">
            <UserOutlined /> <strong>{stats.totalUnique}</strong> {t.app.redirects.unique}
          </span>
          <span className="redirects-global-stat">
            <ShareAltOutlined /> <strong>{stats.linkCount}</strong> {t.app.redirects.links}
          </span>
        </div>
      </div>

      <Modal
        title={t.app.redirects.modalTitle}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={480}
        className="redirects-form-modal"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="slug"
            label={t.app.redirects.slugLabel}
            rules={[
              { required: true, message: t.app.redirects.slugRequired },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: t.app.redirects.slugPattern },
              { max: 64, message: t.app.redirects.slugMax },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (RESERVED_SLUGS.has(value.trim().toLowerCase())) {
                    return Promise.reject(new Error(t.app.redirects.slugReserved));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t.app.redirects.slugPlaceholder} />
          </Form.Item>
          <Form.Item
            name="url"
            label={t.app.redirects.urlLabel}
            rules={[
              { required: true, message: t.app.redirects.slugRequired },
              { type: 'url', message: t.app.redirects.urlInvalid },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    const u = new URL(value.trim());
                    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
                      return Promise.reject(new Error(t.app.redirects.urlHttpOnly));
                    }
                    if (value.length > 2048) return Promise.reject(new Error(t.app.redirects.urlTooLong));
                  } catch {
                    return Promise.reject(new Error(t.app.redirects.urlInvalid));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t.app.redirects.urlPlaceholder} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={submitting} icon={<PlusOutlined />} block>
              {t.app.redirects.createButton}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <div className="redirects-list">
        {loading ? (
          <div className="redirects-loading">{t.app.redirects.loading}</div>
        ) : list.length === 0 ? (
          <div className="redirects-empty">{t.app.redirects.empty}</div>
        ) : (
          list.map((r) => {
            const total = r.totalClicks ?? 0;
            const unique = r.uniqueClicks ?? 0;
            const avg = unique > 0 ? (total / unique).toFixed(1) : '–';
            return (
              <Card key={r.id} className="redirect-link-card">
                <div className="redirect-link-header">
                  <div className="redirect-link-slug">
                    <code>/r/{r.slug}</code>
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyLink(r.slug)} className="redirect-copy-btn" title={t.app.redirects.copyLink} />
                  </div>
                  <div className="redirect-link-meta">
                    <span className="redirect-link-date">{new Date(r.createdAt).toLocaleDateString('fr-FR', { dateStyle: 'medium' })}</span>
                    <Popconfirm title={t.app.redirects.deleteTitle} onConfirm={() => onDelete(r.id)} okText={t.app.redirects.yes} cancelText={t.app.redirects.no}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} className="redirect-delete-btn" title={t.app.redirects.delete} />
                    </Popconfirm>
                  </div>
                </div>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="redirect-link-url">
                  {r.url}
                </a>
                <div className="redirect-link-stats">
                  <div className="redirect-link-stat">
                    <span className="redirect-link-stat-value">{total}</span>
                    <span className="redirect-link-stat-label">{t.app.redirects.totalClicks}</span>
                  </div>
                  <div className="redirect-link-stat">
                    <span className="redirect-link-stat-value">{unique}</span>
                    <span className="redirect-link-stat-label">{t.app.redirects.uniqueClicks}</span>
                  </div>
                  <div className="redirect-link-stat">
                    <span className="redirect-link-stat-value">{avg}</span>
                    <span className="redirect-link-stat-label">{t.app.redirects.avgClicks}</span>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Redirects;
